import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';
import { environment } from '@env/environment';
import { DialogService } from '@shared/services/dialog/dialog.service';
import { WarningModalComponent } from '@app/@shared/components/warning-modal/warning-modal.component';
import { PlatformEntitlementRequestModalComponent } from '@app/@shared/components/platform-entitlement-request-modal/platform-entitlement-request-modal.component';
import {
  SubscriptionCard,
  Entitlement,
  AnalyticsMenu,
  SubscriptionItem
} from '@app/@shared/models/shared.models';
import { CustomerType, ReportAccess, ReportEntitlements } from '@app/@shared/models';
import { InsideTechEntitlement } from '@app/@shared/interfaces/inside-tech-entitlement.interface';
import { FeatureFlagService, ffKeys } from '@shared/services/featureflag.service';
import {
  SocialUserRequestModalComponent
} from '@shared/components/social-user-request-modal/social-user-request-modal.component';
import { VendorIntegrationService } from '@shared/services/vendor-integration.service';
import { WindowService } from '@shared/services/window-location/window.service';
import { AuthenticationService } from '@app/auth';

@Injectable({
  providedIn: 'root',
})
export class EntitlementService {
  private readonly HEADERS = new HttpHeaders().set('Accept', 'application/json');
  private readonly entitlementUrl = environment.entitlementServiceUrl;
  private readonly metaUrl = environment.metaServiceBaseUrl;

  constructor(
    private readonly http: HttpClient,
    protected readonly dialogService: DialogService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly vendorIntegrationService: VendorIntegrationService,
    private readonly authService: AuthenticationService,
    private readonly windowService: WindowService
  ) { }

  getReportAccess(reportCode: string): Observable<ReportAccess> {
    const url = `${this.entitlementUrl}/report/validate/${reportCode}`;
    const headers = new HttpHeaders().set('Accept', 'application/json');
    const params = new HttpParams().set('url', environment.basePath);
    return this.http.get<ReportAccess>(url, { params, headers });
  }

  getAnalyticsEntitlements(): Observable<AnalyticsMenu> {
    const url = `${this.entitlementUrl}/subscriber/tableauAnalyticsChannelsEntitlement`;
    return this.http.get<any>(url);
  }

  getEntitlementList(): Observable<Array<Entitlement>> {
    const url = `${this.entitlementUrl}/entitlement-list`;
    return this.http.get<any>(url)
      .pipe(
        map(({ entitlements }) => entitlements)
      );
  }

  postAccessRequest(requestedItem: string, comment?: string): Observable<boolean> {
    // Validate requestedItem before submission to prevent "undefined" in Salesforce
    if (!requestedItem || requestedItem.toLowerCase().includes('undefined')) {
      requestedItem = this.windowService.href || 'Unknown Content';
    }

    return this.authService.user$.pipe(concatMap(user => {
      return this.vendorIntegrationService.submitPardotForm(environment.pardotForms.accessRequestForm, {
        username: user.username,
        requestedItem,
        comment,
        referralUrl: this.windowService.href,
      });
    }))
  }

  postUnwatermarkedImageRequest(imageUrl: string, clObjectId: string, comment: string): Observable<boolean> {
    const url = `${this.metaUrl}/assetDownloadRequest/unwatermarkedImage`;
    const headers = new HttpHeaders().set('Accept', 'application/json');
    const params = new HttpParams().set('comment', comment);

    const body = {
      url: imageUrl,
      clObjectId,
    }

    return this.http.post<any>(url, body, { headers, params });
  }

  getCustomerType(): Observable<{ type: CustomerType }> {
    const url = `${this.entitlementUrl}/user/me/type`;
    const headers = new HttpHeaders().set('Accept', 'application/json');

    return this.http.get<any>(url, { headers });
  }

  openRequestAccessConfirmDialog(reportTitle: string, onClose?: () => void): void {
    // Validate reportTitle to prevent "undefined" from appearing in Salesforce
    if (!reportTitle || reportTitle.trim() === '' || reportTitle.toLowerCase().includes('undefined')) {
      reportTitle = this.windowService.href || 'Unknown Content';
    }

    this.featureFlagService.getFlag(ffKeys.enableSocialUsersPermanent).subscribe(flag => {
      const requestModalInstance = flag ? this.openSocialEmailModal() : this.openWarningModalWindow();
      requestModalInstance.componentInstance.props = {
        title: 'Your subscription does not include access to this content.',
        titleIconClass: 'icon-unentitled',
        body: 'Would you like to request access?',
        closeButtonLabel: 'Close',
        actionButtonLabel: 'Request Access',
      };
      requestModalInstance.componentInstance.reportTitle = reportTitle;
      requestModalInstance.result.then(() => {
        this.openRequestAccessFormDialog(reportTitle, onClose);
      }, onClose);
    })
  }

  openRequestAccessFormDialog(reportTitle: string, onClose?: () => void): void {
    this.featureFlagService.getFlag(ffKeys.enableSocialUsersPermanent).subscribe(flag => {
      const requestFormModalInstance = flag ? this.openSocialEmailModal() : this.openPlatformEntitlementRequestModal();
      requestFormModalInstance.componentInstance.reportTitle = reportTitle;
      requestFormModalInstance.result.then((formData) => {
        this.postAccessRequest(formData.subject, formData.topic)
          .pipe(
            catchError(() => {
              this.dialogService.openError();
              return of(null);
            })
          )
          .subscribe((success) => {
            if (success) {
              const modalServiceRef = this.dialogService.openGeneric({
                title: 'Entitlement Request sent!',
                body: 'Our team has received your request ' +
                  'and will process your inquiry shortly. ' +
                  'We are looking forward to helping you!',
                closeButtonLabel: 'Done',
              });
              modalServiceRef.result.then(() => { }, onClose)
            }
          });
      }, onClose);
    });
  }

  public displayEntitlementRequestPanel(subscriptionCard: SubscriptionCard, subscriptionEnable: boolean = false) {
    const modalInstance = this.dialogService.open(WarningModalComponent, {
      size: 'sm',
      centered: true,
      windowClass: 'uui-modal',
    });
    modalInstance.componentInstance.props = {
      title: 'Your subscription does not include access to this content.',
      titleIconClass: 'icon-unentitled',
      body: !subscriptionEnable ? 'Would you like us to contact you with more information about this subscription?' : '',
      closeButtonLabel: 'Maybe Later',
      actionButtonLabel: 'Contact Me',
      isSubscription: subscriptionEnable
    }
    modalInstance.result
      .then(() => {
        this.postAccessRequest(subscriptionCard.name)
          .pipe(
            catchError(() => {
              this.dialogService.openError();
              return of(null);
            })
          )
          .subscribe((success) => {
            if (success) {
              this.dialogService.openGeneric({
                title: 'Your request has been sent!',
                body: 'Our team has received your request for more ' +
                  'information and will process your inquiry within 2 business days. ' +
                  'We are looking forward to helping you!',
                closeButtonLabel: 'Done',
              });
            }
          });
      });
  }

  /**
   * Returns ReportEntitlement object from a report id
   *
   * @param reportId a report id
   */
  public getReportEntitlements(reportId: string): Observable<ReportEntitlements> {
    return this.http.get<any>(`${this.entitlementUrl}/report/${reportId}`, { headers: this.HEADERS })
      .pipe(
        catchError(() => {
          return of({
            id: '',
            name: '',
            code: '',
            deleted: false,
            sf_id_product2: '',
            download_enabled: {
              pdf: false,
              image: false,
              data_grid: false,
              circuit_vision: false,
            }
          })
        })
      );
  }

  public isTechLibraryImagesDownloadEnabled(): Observable<boolean> {
    return this.http.get<any>(`${this.entitlementUrl}/entitlement/insidetech/mine`)
      .pipe(
        map(({ download }) => download),
      );
  }

  public getTechLibraryEntitlement(): Observable<boolean> {
    const url = `${this.entitlementUrl}/entitlement/insidetech/mine`;
    const headers = new HttpHeaders().set('Accept', 'application/json');
    const params = new HttpParams().set('url', environment.basePath);
    return this.http.get<any>(url, { params, headers }).pipe(
      map((result: InsideTechEntitlement) => !!result?.assetAvailabilityTimeEnum || !!result?.genealogy
      ));
  }

  getSubscriptionItems(customerSegment?: string): Observable<SubscriptionItem[]> {
    const url = `${this.entitlementUrl}/entitlement/subscription-items`;
    const headers = new HttpHeaders().set('Accept', 'application/json');
    const params = customerSegment ? new HttpParams().set('customerSegment', customerSegment) : undefined;
    return this.http.get<SubscriptionItem[]>(url, { headers, params });
  }

  private openSocialEmailModal() {
    return this.dialogService.open(SocialUserRequestModalComponent,
      { size: 'lg', centered: true, windowClass: 'uui-modal' });
  }

  private openPlatformEntitlementRequestModal() {
    return this.dialogService.open(PlatformEntitlementRequestModalComponent,
      { size: 'lg', centered: true, fullscreen: true, windowClass: 'uui-modal' });
  }

  private openWarningModalWindow() {
    return this.dialogService.open(WarningModalComponent,
      { size: 'sm', centered: true, windowClass: 'uui-modal' });
  }
}
